// src/pages/FindCancerCentersHub.jsx
// Complete Hub for all 72 NCI-Designated Cancer Centers, CoC Programs, and NAPT Proton Therapy Centers.

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  NCI_CANCER_CENTERS,
  NAPT_PROTON_CENTERS,
  CANCER_CENTER_TECHNOLOGIES,
} from '../data/cancerCentersDirectoryData';
import { US_STATES } from '../data/usStatesData';

export default function FindCancerCentersHub() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('ALL');
  const [selectedTech, setSelectedTech] = useState('ALL');

  const filteredCenters = useMemo(() => {
    return NCI_CANCER_CENTERS.filter((c) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.state.toLowerCase().includes(q) ||
        (c.keySpecialties && c.keySpecialties.some((s) => s.toLowerCase().includes(q)));

      const matchesState = selectedState === 'ALL' || c.stateSlug === selectedState;

      const matchesTech =
        selectedTech === 'ALL' ||
        (selectedTech === 'proton' && (c.hasProton || (c.technologies && c.technologies.includes('Proton Therapy')))) ||
        (selectedTech === 'cart' && c.technologies && c.technologies.includes('CAR-T Cell Therapy')) ||
        (selectedTech === 'cyberknife' && c.technologies && c.technologies.includes('CyberKnife SBRT')) ||
        (selectedTech === 'robotic' && c.technologies && c.technologies.includes('Robotic Surgery')) ||
        (selectedTech === 'phase1' && c.technologies && c.technologies.includes('Phase I Unit'));

      return matchesSearch && matchesState && matchesTech;
    });
  }, [searchQuery, selectedState, selectedTech]);

  // Schema structured data
  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: 'Directory of all 72 NCI-Designated Cancer Centers & Proton Therapy Centers | ByOnco',
    url: 'https://www.getbyonco.com/find-cancer-centers',
    description:
      'Search all 72 NCI-Designated Comprehensive Cancer Centers and NAPT proton therapy centers in the US. Filter by state, proton therapy, CAR-T, CyberKnife, and clinical trials.',
    publisher: {
      '@type': 'Organization',
      name: 'ByOnco',
      url: 'https://www.getbyonco.com',
    },
  };

  const jsonLdItemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'NCI-Designated Cancer Centers in the United States',
    itemListElement: filteredCenters.slice(0, 30).map((center, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'MedicalOrganization',
        name: center.name,
        url: `https://www.getbyonco.com/find-cancer-centers/${center.slug}`,
        telephone: center.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: center.address,
          addressLocality: center.city,
          addressRegion: center.stateAbbr,
          postalCode: center.zip,
          addressCountry: 'US',
        },
      },
    })),
  };

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900 pt-24 pb-20">
      <Helmet>
        <title>All 72 NCI-Designated Cancer Centers &amp; Proton Therapy Directory | ByOnco</title>
        <meta
          name="description"
          content="Find all 72 NCI-Designated Comprehensive Cancer Centers and accredited proton centers in the United States. Filter by state, CAR-T, proton therapy, CyberKnife, and clinical trial volume."
        />
        <link rel="canonical" href="https://www.getbyonco.com/find-cancer-centers" />
        <script type="application/ld+json">{JSON.stringify(jsonLdWebPage)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdItemList)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 mb-4 border border-emerald-200">
            <span>Official US Oncology Directory</span>
            <span>•</span>
            <span>All 72 NCI Centers + NAPT Proton Facilities</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
            Find NCI-Designated Cancer Centers &amp; Advanced Proton Facilities
          </h1>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            NCI-designated cancer centers represent the top 4% of cancer hospitals in the United States, delivering up to 25% superior 5-year survival rates, dedicated Phase I novel drug units, and subspecialty multidisciplinary tumor boards.
          </p>

          {/* Search & Filter Bar */}
          <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200/80 mb-8 text-left">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Search Hospital or Specialty
                </label>
                <input
                  type="text"
                  placeholder="e.g. MD Anderson, Sarcoma, Boston..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Filter By State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
                >
                  <option value="ALL">All 50 States + DC</option>
                  {US_STATES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name} ({s.abbr})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Filter By Advanced Technology
                </label>
                <select
                  value={selectedTech}
                  onChange={(e) => setSelectedTech(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
                >
                  <option value="ALL">All Advanced Technologies</option>
                  {CANCER_CENTER_TECHNOLOGIES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label} ({t.tag})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Filter Badges */}
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2 items-center text-xs">
              <span className="font-semibold text-slate-600">Quick Filters:</span>
              <button
                onClick={() => { setSelectedTech('proton'); setSelectedState('ALL'); }}
                className={`px-3 py-1 rounded-lg border transition ${selectedTech === 'proton' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'}`}
              >
                Proton Therapy Centers
              </button>
              <button
                onClick={() => { setSelectedTech('cart'); setSelectedState('ALL'); }}
                className={`px-3 py-1 rounded-lg border transition ${selectedTech === 'cart' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'}`}
              >
                CAR-T Cell Therapy
              </button>
              <button
                onClick={() => { setSelectedTech('phase1'); setSelectedState('ALL'); }}
                className={`px-3 py-1 rounded-lg border transition ${selectedTech === 'phase1' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'}`}
              >
                Phase I Clinical Trials
              </button>
              {(searchQuery || selectedState !== 'ALL' || selectedTech !== 'ALL') && (
                <button
                  onClick={() => { setSearchQuery(''); setSelectedState('ALL'); setSelectedTech('ALL'); }}
                  className="text-emerald-700 hover:text-emerald-900 font-semibold underline ml-auto"
                >
                  Reset All Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Results Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">
            NCI-Designated Cancer Centers ({filteredCenters.length} centers found)
          </h2>
          <span className="text-sm text-slate-500">
            Showing official NCI Comprehensive, Clinical, and Laboratory Centers
          </span>
        </div>

        {filteredCenters.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-2">No cancer centers match your current filter</h3>
            <p className="text-slate-600 mb-4 text-sm">
              Try broadening your search term or resetting the technology/state filters.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedState('ALL'); setSelectedTech('ALL'); }}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCenters.map((center) => (
              <div
                key={center.slug}
                className="bg-white rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition flex flex-col p-6"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                    {center.state} ({center.stateAbbr})
                  </span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded text-slate-600 bg-slate-100">
                    {center.city}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                  <Link
                    to={`/find-cancer-centers/${center.slug}`}
                    className="hover:text-emerald-700 transition"
                  >
                    {center.name}
                  </Link>
                </h3>

                <p className="text-xs font-medium text-emerald-700 mb-3">
                  {center.designation}
                </p>

                <p className="text-xs text-slate-600 line-clamp-3 mb-4 leading-relaxed">
                  {center.description}
                </p>

                {/* Specialties & Tech Tags */}
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {(center.technologies || []).slice(0, 3).map((tech, idx) => (
                    <span
                      key={idx}
                      className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                  {center.hasProton && (
                    <span className="text-[11px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-medium border border-sky-200">
                      ⚡ Proton Center
                    </span>
                  )}
                </div>

                {/* Clinical Trials Badge */}
                <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                  <div className="font-semibold text-slate-800">
                    {center.clinicalTrialsCount}
                  </div>
                  <a
                    href={`tel:${center.phone.replace(/[^0-9]/g, '')}`}
                    className="text-emerald-700 font-semibold hover:underline"
                  >
                    {center.phone}
                  </a>
                </div>

                <div className="mt-4 flex gap-2">
                  <Link
                    to={`/find-cancer-centers/${center.slug}`}
                    className="flex-1 text-center py-2 px-3 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition"
                  >
                    View Details
                  </Link>
                  <Link
                    to="/cancer-second-opinion"
                    className="text-center py-2 px-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold hover:bg-emerald-100 transition"
                  >
                    Get 2nd Opinion
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NAPT Proton Centers Section */}
      <div className="bg-slate-900 text-white py-16 mb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="text-xs font-bold tracking-widest text-emerald-400 uppercase">
              Sub-Millimeter Radiation Precision
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-1 mb-3">
              National Association for Proton Therapy (NAPT) Centers
            </h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              Proton beam radiation deposits the bulk of its energy inside the tumor without an exit dose (the Bragg Peak), significantly reducing radiation exposure to critical organs like the heart, spinal cord, and brain tissue.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {NAPT_PROTON_CENTERS.map((proton, idx) => (
              <div
                key={idx}
                className="bg-slate-800/80 border border-slate-700/80 rounded-xl p-4 flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-semibold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/50">
                      {proton.state}
                    </span>
                    <span className="text-xs text-slate-400">{proton.city}</span>
                  </div>
                  <h3 className="font-bold text-white text-sm mb-1">{proton.name}</h3>
                  <p className="text-xs text-slate-300 mb-3">{proton.affiliation}</p>
                </div>
                <Link
                  to={`/cancer-centers/${proton.stateSlug}`}
                  className="text-xs text-emerald-400 hover:text-emerald-300 font-medium inline-flex items-center gap-1"
                >
                  View State Centers &rarr;
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Educational Guide: NCI Designation vs Community Care */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Why Choose an NCI-Designated Cancer Center?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-lg mb-3">
                1
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Proven Survival Benefit</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Peer-reviewed studies published in the <em>Journal of Clinical Oncology</em> demonstrate that newly diagnosed cancer patients treated at NCI-designated comprehensive cancer centers experience up to 25% lower long-term mortality.
              </p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-lg mb-3">
                2
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Exclusive Clinical Trial Access</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                NCI centers host extensive Phase I and Phase II clinical trial pipelines, granting patients early access to revolutionary bispecific antibodies, personalized mRNA neoantigen vaccines, and novel cellular therapies not yet FDA approved for community clinics.
              </p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-lg mb-3">
                3
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Organ-Specific Multidisciplinary Boards</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Cases are reviewed collaboratively by dedicated disease teams including surgical oncologists, radiation physicists, subspecialized molecular pathologists, and medical oncologists who focus 100% of their practice on your exact tumor histology.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Directory by State Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-xl font-bold text-slate-900 mb-4">
          Browse Cancer Centers by State (All 50 States + DC)
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
          {US_STATES.map((s) => (
            <Link
              key={s.slug}
              to={`/cancer-centers/${s.slug}`}
              className="p-2.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 font-medium transition text-center"
            >
              {s.name} ({s.abbr})
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
